import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import { getAudioGraphInstance } from '../audioEngine';
import units from "../units.json"
import { addKeysAsField } from '../utils/misc';
import { SynthWamUI } from '../projectViews/stationViews/SynthWamUI.jsx';
import { sendNoteOnToWamNode, sendNoteOffToWamNode, stopAllNotesForWamNode, createWamNode} from '../utils/wam.js';
import { PianoRoller } from './PianoRoller.js';
import colors from "../../styles/colors.module.scss";


export class SynthWam extends InstrumentBase {
    static metadata = {
        tipus: 'synth_wam',
        tipusDisplay: 'Synth',
        versio: '0.1',
        color:  colors.red,
        hasLogo: false,
        hasMiniature: false,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        ...PianoRoller.parametersDescription,
        env_attack: {type: 'float', unit: units.second, min: 0.0, max: 2.0, initial: 0.1, bind: "preset"},
        env_decay: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 0.5, bind: "preset"},
        env_sustain: {type: 'float', min: 0.0, max: 1.0, initial: 0.8, bind: "preset"},
        env_release: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 2.0, bind: "preset"},
        osc_waveform: {type: 'enum', options: ['sine', 'triangle', 'square', 'sawtooth'], initial: 'sawtooth', bind: "preset"},
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 100, max: 15000, initial: 6000, logarithmic: true, bind: "preset"},
        lpf_q: {type: 'float', min: 0.0, max: 1.0, initial: 0.7, bind: "preset"},
        lfo_rate: {type: 'float', unit: units.hertz, min: 0.1, max: 50.0, initial: 0.3, logarithmic: true, bind: "preset"},
        lfo_depth: {type: 'float', min: 0.0, max: 2000.0, initial: 300.0, bind: "preset"},
        osc_octave: {type: 'int', min: -2, max: 2, step: 1, initial: 0, bind: "preset"},
        fx_distortion_amount: {type: 'float', min: 0.0, max: 100.0, initial: 0.0, bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        typical: {min: 60, max: 83}
    }

    getUserInterfaceComponent() {
        return SynthWamUI
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const wamUrl = appPrefix + '/static/dist/public/wam/PolySynth/index.js'
        const wamNode = await createWamNode(wamUrl, this.id);
        //wamNode.getParameterInfo().then((params) => console.debug(params));  // Print available WAM node parameters

        const gainOutput = new Tone.Gain(Tone.dbToGain(-12));
        this.audioNodes = {
            gainOutput: gainOutput,
            wamNode: wamNode,
        }
        Tone.connect(this.audioNodes.wamNode, this.audioNodes.gainOutput);

        // Seteja gain general
        this.audioNodes.wamNode.setParameterValues({
            'volume': {value: -3.0},
        })

        // Crea els nodes d'efectes (això també els afegirà al diccionari de nodes de l'instrument)
        this.addEffectChainNodes(this.audioNodes.gainOutput, this.bus);
    }

    setParameterInAudioGraph(name, value) {
        switch (name) {
            case "lpf_cutoff":
                this.audioNodes.wamNode.setParameterValues({
                    'cutoff': {value: value},
                })
                break;
            case "lpf_q":
                this.audioNodes.wamNode.setParameterValues({
                    'resonance': {value: value},
                })
                break;
            case "lfo_rate":
                this.audioNodes.wamNode.setParameterValues({
                    'lfoRate': {value: value},
                })
                break;
            case "lfo_depth":
                this.audioNodes.wamNode.setParameterValues({
                    'lfoAmount': {value: value},
                })
                break;
            case "fx_distortion_amount":
                this.audioNodes.wamNode.setParameterValues({
                    'distortionAmount': {value: value},
                })
                break;
            case "osc_octave":
                this.audioNodes.wamNode.setParameterValues({
                    'octave': {value: value},
                })
                break;
            case "env_attack":
            case "env_decay":
            case "env_sustain":
            case "env_release":
                this.audioNodes.wamNode.setParameterValues({
                    [name.replace("env_", "")]: {value: value},
                })
                break;
            case "osc_waveform":
                this.audioNodes.wamNode.setParameterValues({
                    'waveform': { value: {
                        sawtooth: 3, 
                        triangle: 1, 
                        square: 2, 
                        sine: 0}[value] },
                })
                break;
        }
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        if (!Object.keys(this.audioNodes).length) { return };
        
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        for (let i = 0; i < sequence.length; i++) {
            const note = sequence[i];
            if (note.onset === currentStep) {
                const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
                sendNoteOnToWamNode(this.audioNodes.wamNode, note.pitch, (note.velocity ?? 127), time);
                sendNoteOffToWamNode(this.audioNodes.wamNode, note.pitch, 0, time + duration * getAudioGraphInstance().get16BeatTime());
            }
        }   
    }
    
    onTransportStop() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onStopAllSounds() {
        if (!Object.keys(this.audioNodes).length) { return };
        stopAllNotesForWamNode(this.audioNodes.wamNode, Tone.getContext().currentTime);
    }
    
    onMidiNote(pitch, midiVelocity=127, noteOff, extras) {
        if (!getAudioGraphInstance().isGraphBuilt()) return; 
        if (!Object.keys(this.audioNodes).length) { return };
        if (!noteOff) {
            sendNoteOnToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        } else {
            sendNoteOffToWamNode(this.audioNodes.wamNode, pitch, midiVelocity, Tone.getContext().currentTime);
        }
        if (!extras.skipRecording) this.handlePianoRollRecording(pitch, midiVelocity, noteOff);
    }
}